import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { listAppUsers, updateAppUserStatus, type AppUser, type AppUserStatus } from '../lib/api';
import { theme } from '../theme';

export function AppUsersPage() {
  const [pendingUsers, setPendingUsers] = useState<AppUser[]>([]);
  const [pendingActionUserId, setPendingActionUserId] = useState<string | null>(null);

  // Pending approvals load once on mount + after each approve/disable
  // action. Independent of the main table's status filter so staff always
  // see them at the top, no matter which filter they've applied below.
  const refreshPending = async (): Promise<void> => {
    try {
      const res = await listAppUsers({ status: 'pending-approval', limit: 60 });
      setPendingUsers(res.users);
    } catch {
      // Best-effort; main table still works even if this fails.
      setPendingUsers([]);
    }
  };

  useEffect(() => {
    void refreshPending();
  }, []);

  const decidePending = async (userId: string, status: 'active' | 'disabled'): Promise<void> => {
    setPendingActionUserId(userId);
    try {
      await updateAppUserStatus(userId, status);
      setPendingUsers((prev) => prev.filter((u) => u.userId !== userId));
    } finally {
      setPendingActionUserId(null);
    }
  };

  const [users, setUsers] = useState<AppUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [statusFilter, setStatusFilter] = useState<'' | AppUserStatus>('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    setLoading(true);
    listAppUsers({ status: statusFilter || undefined, limit: 50 })
      .then((res) => {
        setUsers(res.users);
        setNextCursor(res.nextCursor);
        setError(null);
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [statusFilter]);

  const loadMore = async () => {
    if (!nextCursor) return;
    const res = await listAppUsers({
      status: statusFilter || undefined,
      cursor: nextCursor,
      limit: 50,
    });
    setUsers((prev) => [...prev, ...res.users]);
    setNextCursor(res.nextCursor);
  };

  // Client-side filter — search is over the rows already loaded for instant
  // feedback. Backend pagination still applies underneath.
  const filtered = searchTerm
    ? users.filter((u) => {
        const hay =
          `${u.phoneE164} ${u.firstName} ${u.lastName} ${u.rooftopName ?? ''}`.toLowerCase();
        return hay.includes(searchTerm.toLowerCase());
      })
    : users;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 16 }}>
        <h1 style={{ marginRight: 'auto' }}>App Users</h1>
        <Link
          to="/app-users/new"
          style={{
            padding: '8px 14px',
            background: theme.brand.primary,
            color: theme.text.onBrand,
            borderRadius: theme.radius.md,
            fontSize: 13,
            fontWeight: 600,
            textDecoration: 'none',
          }}
        >
          + New user
        </Link>
      </div>
      <p style={{ color: theme.text.secondary, fontSize: 13 }}>
        Mobile app users who upload OBD scans. Approve / disable / re-enable here.
      </p>
      {pendingUsers.length > 0 && (
        <PendingApprovalsSection
          users={pendingUsers}
          pendingActionUserId={pendingActionUserId}
          onDecide={decidePending}
        />
      )}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as '' | AppUserStatus)}
        >
          <option value="">All statuses</option>
          <option value="pending-approval">Pending approval</option>
          <option value="active">Active</option>
          <option value="disabled">Disabled</option>
        </select>
        <input
          placeholder="Search name / phone / rooftop…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ flex: 1, maxWidth: 320 }}
        />
      </div>
      {error && <p style={{ color: theme.status.error }}>Error: {error}</p>}
      {loading && <p>Loading…</p>}
      {!loading && filtered.length === 0 && <p>No users match this filter.</p>}
      {!loading && filtered.length > 0 && (
        <>
          <AppUsersTable users={filtered} />
          {nextCursor && (
            <button
              onClick={loadMore}
              style={{
                marginTop: 16,
                padding: '8px 16px',
                background: theme.surface.panel,
                border: `1px solid ${theme.surface.borderStrong}`,
                color: theme.text.primary,
                borderRadius: theme.radius.md,
              }}
            >
              Load more
            </button>
          )}
        </>
      )}
    </div>
  );
}

function AppUsersTable({ users }: { users: AppUser[] }) {
  return (
    <table style={{ width: '100%' }}>
      <thead>
        <tr style={{ background: theme.surface.header }}>
          <Th>Name</Th>
          <Th>Phone</Th>
          <Th>Rooftop</Th>
          <Th>Status</Th>
          <Th>Joined</Th>
          <Th>Actions</Th>
        </tr>
      </thead>
      <tbody>
        {users.map((u) => (
          <tr key={u.userId} style={{ borderBottom: `1px solid ${theme.surface.border}` }}>
            <Td>
              {u.firstName || u.lastName ? (
                `${u.firstName} ${u.lastName}`.trim()
              ) : (
                <em style={{ color: theme.text.tertiary }}>(no name)</em>
              )}
            </Td>
            <Td style={{ fontFamily: theme.fonts.mono, fontSize: 12 }}>{u.phoneE164}</Td>
            <Td>{u.rooftopName || <span style={{ color: theme.text.tertiary }}>—</span>}</Td>
            <Td>
              <StatusBadge status={u.status} />
            </Td>
            <Td>{formatRelative(u.createdAt)}</Td>
            <Td>
              <Link to={`/app-users/${encodeURIComponent(u.userId)}`}>Open</Link>
            </Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function StatusBadge({ status }: { status: AppUserStatus }) {
  const palette: Record<AppUserStatus, { bg: string; fg: string; label: string }> = {
    'pending-approval': { bg: theme.status.warning, fg: theme.text.onBrand, label: 'Pending' },
    active: { bg: theme.status.success, fg: theme.text.onBrand, label: 'Active' },
    disabled: { bg: theme.text.tertiary, fg: theme.text.primary, label: 'Disabled' },
  };
  const { bg, fg, label } = palette[status];
  return (
    <span
      style={{
        background: bg,
        color: fg,
        padding: '2px 8px',
        borderRadius: theme.radius.sm,
        fontSize: 11,
        fontWeight: 700,
      }}
    >
      {label}
    </span>
  );
}

function PendingApprovalsSection({
  users,
  pendingActionUserId,
  onDecide,
}: {
  users: AppUser[];
  pendingActionUserId: string | null;
  onDecide: (userId: string, status: 'active' | 'disabled') => Promise<void>;
}) {
  return (
    <div
      style={{
        marginBottom: 24,
        padding: '12px 14px',
        border: `1px solid ${theme.surface.borderStrong}`,
        borderRadius: theme.radius.lg,
        background: theme.surface.panel,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <h2 style={{ margin: 0, fontSize: 16 }}>Pending approvals</h2>
        <span
          style={{
            background: theme.status.warning,
            color: theme.text.onBrand,
            padding: '1px 8px',
            borderRadius: theme.radius.sm,
            fontWeight: 700,
            fontSize: 11,
          }}
        >
          {users.length}
        </span>
        <span style={{ color: theme.text.tertiary, fontSize: 12 }}>
          Self-signed-up via mobile — approve to grant scan-upload access; disable to deny.
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {users.map((u) => (
          <div
            key={u.userId}
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr auto auto auto',
              gap: 12,
              alignItems: 'center',
              padding: '10px 12px',
              border: `1px solid ${theme.surface.border}`,
              borderRadius: theme.radius.md,
              background: theme.surface.header,
            }}
          >
            <div>
              <div style={{ fontWeight: 600, fontSize: 13 }}>
                {u.firstName || u.lastName ? (
                  `${u.firstName} ${u.lastName}`.trim()
                ) : (
                  <em style={{ color: theme.text.tertiary }}>(no name)</em>
                )}
              </div>
              <div
                style={{
                  fontFamily: theme.fonts.mono,
                  fontSize: 11,
                  color: theme.text.secondary,
                }}
              >
                {u.phoneE164}
              </div>
              <div style={{ fontSize: 11, color: theme.text.tertiary }}>
                Joined {new Date(u.createdAt).toLocaleString()}
              </div>
            </div>
            <Link to={`/app-users/${encodeURIComponent(u.userId)}`} style={{ fontSize: 12 }}>
              Details
            </Link>
            <button
              onClick={() => void onDecide(u.userId, 'active')}
              disabled={pendingActionUserId === u.userId}
              style={{
                padding: '6px 12px',
                background: theme.brand.primary,
                color: theme.text.onBrand,
                border: 'none',
                borderRadius: theme.radius.md,
                fontWeight: 600,
                fontSize: 12,
                cursor: 'pointer',
                opacity: pendingActionUserId === u.userId ? 0.5 : 1,
              }}
            >
              Approve
            </button>
            <button
              onClick={() => void onDecide(u.userId, 'disabled')}
              disabled={pendingActionUserId === u.userId}
              style={{
                padding: '6px 12px',
                background: 'transparent',
                color: theme.status.error,
                border: `1px solid ${theme.status.errorBorder}`,
                borderRadius: theme.radius.md,
                fontSize: 12,
                cursor: 'pointer',
                opacity: pendingActionUserId === u.userId ? 0.5 : 1,
              }}
            >
              Disable
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      style={{
        textAlign: 'left',
        padding: '8px 12px',
        fontSize: 12,
        color: theme.text.secondary,
        fontWeight: 600,
      }}
    >
      {children}
    </th>
  );
}

function Td({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <td style={{ padding: '8px 12px', fontSize: 13, ...style }}>{children}</td>;
}

function formatRelative(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return date.toLocaleDateString();
}
